import { useEffect, useRef, useState } from "react";
import { Send, ThumbsUp, ThumbsDown, X, MessageCircle, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/flow";
import { track } from "@/lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_WA = "+393932543344";
const SUPPORT_EMAIL = "info@fineskinbeauty.com";

const AVATAR = "/consultant-avatar.jpg";
const SPECIALIST = "/consultant-specialist.jpg";

const renderText = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith("**") && part.endsWith("**") ? <strong key={`${i}-${part}`} className="font-medium text-[#1A1A18]">{part.slice(2, -2)}</strong> : part);

const Bubble = ({ m, onVote }) => {
  const mine = m.role === "user";
  return (
    <div className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`consultant-msg-${m.role}`}>
      {!mine && <img src={AVATAR} alt="" className="h-7 w-7 rounded-full object-cover shrink-0 border border-[#E8DEC8]" />}
      <div className={`max-w-[85%] sm:max-w-[78%] ${mine ? "bg-[#1A1A18] text-[#FAF8F5] rounded-2xl rounded-br-sm" : "bg-white border border-[#E6DFD5] text-[#2B2A27] rounded-2xl rounded-bl-sm"} px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap`}>
        {m.content ? renderText(m.content) : <span className="inline-flex gap-1 items-center text-[#8C8275]" data-testid="consultant-typing"><span className="animate-pulse">●</span><span className="animate-pulse [animation-delay:150ms]">●</span><span className="animate-pulse [animation-delay:300ms]">●</span><span className="ml-2 text-xs">sta scrivendo…</span></span>}
        {!mine && m.kind !== "welcome" && m.done && (
          <div className="mt-2 flex gap-1 -ml-1" data-testid={`consultant-feedback-${m.id}`}>
            {[[1, ThumbsUp, "Utile"], [-1, ThumbsDown, "Non utile"]].map(([v, Icon, label]) => (
              <button key={v} data-testid={`consultant-vote-${v > 0 ? "up" : "down"}-${m.id}`} aria-label={label} onClick={() => onVote(m.id, v)}
                className={`p-1.5 rounded-full transition-colors ${m.vote === v ? "bg-[#C5A059]/20 text-[#8C6F3E]" : "text-[#B8B0A4] hover:text-[#8C6F3E]"}`}>
                <Icon size={14} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ConsultantChat = ({ profile, routine, chosen }) => {
  const sessionId = getSessionId();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
    const ctx = () => ({
    session_id: sessionId, profile: { skin_type: profile.skin_type, needs: profile.needs, sensitivity: profile.sensitivity, eye: profile.eye, conditions: profile.conditions, blocked_actives: profile.blocked_actives, allergens: profile.allergens, experience: profile.experience, routine_mode: profile.routine_mode, notes: profile.notes },
    routine: Object.fromEntries(["day", "night"].map((m) => [m, routine[m].map((e) => ({ step_key: e.step_key, step_name: e.step_name, product_id: chosen(m, e), missing: !!e.missing })).filter((e) => e.product_id || e.missing)])),
  });

  useEffect(() => {
    let alive = true;
    api.post("/consultant/welcome", ctx()).then((r) => { if (!alive) return; setMessages(r.data.messages.map((m) => ({ ...m, done: true }))); setRemaining(r.data.remaining); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);
  useEffect(() => { document.body.style.overflow = expanded ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [expanded]);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    const tempId = `tmp-${Date.now()}`;
    let id = tempId;
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: q, done: true }, { id: tempId, role: "assistant", content: "", done: false }]);
    track("consultant_question", { length: q.length });
    try {
      const res = await fetch(`${BACKEND_URL}/api/consultant/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...ctx(), text: q }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => m.map((x) => x.id === tempId ? { ...x, content: err.detail || "Non riesco a rispondere ora. Riprova tra poco.", done: true, kind: "error" } : x));
        return;
      }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const frames = buf.split("\n\n"); buf = frames.pop();
        for (const f of frames) {
          const ev = (f.match(/^event: (\w+)/m) || [])[1];
          const data = (f.match(/^data: (.*)$/m) || [])[1];
          if (!data) continue;
          const d = JSON.parse(data);
          if (ev === "meta") { const prev = id; id = d.id; setRemaining(d.remaining); setMessages((m) => m.map((x) => x.id === prev ? { ...x, id: d.id } : x)); }
          else if (ev === "done") { const cur = id; setMessages((m) => m.map((x) => x.id === cur ? { ...x, done: true } : x)); }
          else if (d.t) { const cur = id; setMessages((m) => m.map((x) => x.id === cur ? { ...x, content: x.content + d.t } : x)); }
        }
      }
      setMessages((m) => m.map((x) => x.id === id || x.id === tempId ? { ...x, done: true } : x));
    } catch {
      setMessages((m) => m.map((x) => (x.id === tempId || x.id === id) && !x.content ? { ...x, content: "Connessione interrotta. Riprova tra poco.", done: true } : x));
    } finally { setBusy(false); }
  };

  const vote = (id, v) => {
    setMessages((m) => m.map((x) => x.id === id ? { ...x, vote: v } : x));
    api.post("/consultant/feedback", { session_id: sessionId, message_id: id, vote: v }).then(() => toast.success(v > 0 ? "Grazie del feedback!" : "Grazie, ci aiuta a migliorare")).catch(() => {});
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };
  const limitReached = remaining === 0;
  const shell = expanded ? "fixed inset-0 z-[60] bg-[#FAF8F5] flex flex-col sm:static sm:z-auto sm:flex sm:flex-col" : "flex flex-col";

  return (
    <section className="mt-16" data-testid="consultant-section">
      {!expanded && (
        <div className="relative overflow-hidden rounded-t-[28px] bg-[#F2ECE4] border border-b-0 border-[#E6DFD5]" data-testid="consultant-hero">
          <div className="absolute -top-16 -left-16 h-56 w-56 rounded-full bg-[#C5A059]/15 blur-2xl" />
          <div className="hidden sm:block absolute right-[220px] top-6 h-48 w-48 rounded-full border border-[#C5A059]/40" />
          <div className="relative grid grid-cols-[minmax(0,1fr)_128px] sm:grid-cols-[minmax(0,1fr)_230px] items-end">
            <div className="px-5 sm:px-8 py-7 sm:py-10 min-w-0">
              <p className="fs-eyebrow flex items-center gap-2 text-[#8C6F3E]"><Sparkles size={12} strokeWidth={1.5} className="text-[#C5A059]" /> Consulente FineSkin</p>
              <h2 className="font-serif text-2xl sm:text-4xl mt-3 leading-[1.1]" data-testid="consultant-title">Sveta, la tua Beauty Specialist,<br className="hidden sm:block" /> è qui per te.</h2>
              <p className="text-sm text-[#595652] mt-3 font-light leading-relaxed max-w-sm">Dubbi su un prodotto, sull'ordine degli step o su come abbinarli? Chiedi a Sveta: ti risponde subito, pensando alla tua pelle.</p>
              <span className="mt-5 inline-flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-[#1A1A18] bg-white/70 border border-[#E6DFD5] rounded-full px-3 py-1.5" data-testid="consultant-online-badge">
                <span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full rounded-full bg-[#C5A059] opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#C5A059]" /></span>
                Online · risponde subito
              </span>
            </div>
            <img src={SPECIALIST} alt="Sveta, Beauty Specialist FineSkin" data-testid="consultant-specialist-img" className="w-full h-[210px] sm:h-[300px] object-cover object-top self-end [mask-image:linear-gradient(to_right,transparent,black_18%)] motion-safe:animate-[fsFloat_6s_ease-in-out_infinite]" />
          </div>
        </div>
      )}
      <div className={`${shell} border border-[#E6DFD5] bg-white ${expanded ? "" : "rounded-b-[28px] overflow-hidden"}`} data-testid="consultant-chat" data-expanded={expanded}>
        <header className={`px-5 sm:px-6 border-b border-[#F2ECE4] flex items-center justify-between gap-3 bg-[#FAF8F5] ${expanded ? "pt-5 pb-4" : "py-3"}`}>
          <div className="flex items-center gap-3">
            <img src={AVATAR} alt="" className={`rounded-full object-cover border border-[#E8DEC8] ${expanded ? "h-11 w-11" : "h-9 w-9"}`} />
            <div>
              <p className="font-serif text-base leading-tight" data-testid="consultant-name">Sveta</p>
              <p className="text-[11px] text-[#8C8275] flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" /> Beauty Specialist FineSkin · online</p>
            </div>
          </div>
          {expanded && <button data-testid="consultant-collapse-btn" aria-label="Chiudi" onClick={() => setExpanded(false)} className="sm:hidden p-2 -mr-2 text-[#595652]"><X size={20} strokeWidth={1.5} /></button>}
        </header>
        <div ref={listRef} className={`px-4 sm:px-6 py-5 space-y-3 overflow-y-auto ${expanded ? "flex-1" : "max-h-[420px] min-h-[220px]"}`} data-testid="consultant-messages">
          {messages.map((m) => <Bubble key={m.id} m={m} onVote={vote} />)}
          {!messages.length && <p className="text-sm text-[#8C8275]">Il tuo consulente sta arrivando…</p>}
        </div>
        <div className="px-4 sm:px-6 pb-4 pt-3 border-t border-[#F2ECE4] bg-white">
          {limitReached ? (
            <p className="text-sm text-[#595652] py-2" data-testid="consultant-limit">Per oggi abbiamo chiacchierato tanto 🙂 Il consulente torna domani; per urgenze usa il supporto qui sotto.</p>
          ) : (
            <div className="flex items-end gap-2">
              <textarea ref={inputRef} data-testid="consultant-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} onFocus={() => { if (window.innerWidth < 640) setExpanded(true); }}
                rows={1} placeholder="Scrivi qui il tuo dubbio..." disabled={busy}
                className="flex-1 resize-none px-4 py-3 bg-[#FAF8F5] border border-[#E6DFD5] text-[15px] outline-none focus:border-[#C5A059] rounded-2xl max-h-32" />
              <button data-testid="consultant-send-btn" onClick={send} disabled={busy || !text.trim()} aria-label="Invia" className="fs-btn !px-4 !py-3 rounded-full disabled:opacity-40"><Send size={16} strokeWidth={1.5} /></button>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-[#8C8275] leading-snug max-w-sm" data-testid="consultant-disclaimer">Il consulente AI offre consigli cosmetici generali e non sostituisce il parere di un medico o dermatologo.</p>
            <div className="flex items-center gap-2" data-testid="consultant-support">
              <span className="text-[11px] text-[#8C8275]">Parla con il supporto:</span>
              <a data-testid="consultant-support-whatsapp" href={`https://wa.me/${SUPPORT_WA.replace("+", "")}`} target="_blank" rel="noreferrer" aria-label="WhatsApp supporto" className="p-2 border border-[#E6DFD5] rounded-full text-[#595652] hover:border-[#C5A059]"><MessageCircle size={14} strokeWidth={1.5} /></a>
              <a data-testid="consultant-support-email" href={`mailto:${SUPPORT_EMAIL}`} aria-label="Email supporto" className="p-2 border border-[#E6DFD5] rounded-full text-[#595652] hover:border-[#C5A059]"><Mail size={14} strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
